/* eslint-disable max-len */
import { GetR1NetworkResponse } from '@1po/1po-bff-fe-spec/generated/backoffice/r1_network/response/GetR1NetworkResponse';
import { FilterAndSort } from '@1po/1po-bff-fe-spec/generated/common/filter_and_sort/FilterAndSort';
import { FilterArray } from '@1po/1po-bff-fe-spec/generated/common/filter_and_sort/FilterArray';
import { SortBy } from '@1po/1po-bff-fe-spec/generated/common/filter_and_sort/SortBy';
import { FilterAndSortField as MyNetworkClientsFilterAndSortField } from '@1po/1po-bff-fe-spec/generated/my_network/request/model/FilterAndSortField';
import { MyNetworkClientsResponse } from '@1po/1po-bff-fe-spec/generated/my_network/response/MyNetworkClientsResponse';
import { MyNetworkClientUpdatedResponse } from '@1po/1po-bff-fe-spec/generated/my_network/response/MyNetworkClientUpdatedResponse';
import { call, put, takeEvery } from '@redux-saga/core/effects';
import { SagaIterator } from 'redux-saga';
import {
  sendAddEmailToNotificationList,
  sendChangeNotifyOnNewBanner,
  sendChangeNotifyOnNewCountryBanner,
  sendChangeNotifyOnNewCountryPromotion,
  sendChangeNotifyOnNewPromotion,
  sendGetMyNetworkClients,
  sendGetR1NetworkRequest,
  sendRemoveEmailFromNotificationList,
  sendSaveR1Network,
  sendSaveReturnsFeatureSettingsRequest,
  sendSubscribeR1NetworkRequest,
  sendUpdateMyNetworkClients,
} from 'domains/r1Network/R1Network.api';
import { getMyNetworkClientsFilterArray } from 'domains/r1Network/R1Network.mapper';
import * as actions from 'domains/r1Network/R1Network.store';
import {
  getMyNetworkClientsFilters,
  getMyNetworkClientsPagination,
  getMyNetworkClientsSort,
  getR1Network,
  setClientConfig,
  setMyNetworkClients,
  setMyNetworkClientsPagination,
  setMyNetworkClientsStatus,
  setMyNetworkClientsSubmitting,
  setPromotionSettings,
  setR1Network,
  setReturnsFeatureSettings,
  setSelectedClient,
} from 'domains/r1Network/R1Network.store';
import { WsResponse } from 'domains/webSockets/WebSocket.types';
import { notifyTop } from 'UI';
import { AppTranslation, FOUND, LOADING, sagaGuard, select } from 'utils';

export function* getR1NetworkRequestSaga(): SagaIterator {
  yield put(sendGetR1NetworkRequest());
}

export function* getR1NetworkResponseSaga(action: WsResponse<GetR1NetworkResponse>): SagaIterator {
  const {
    isRDReturnFeatureEnabled,
    isRDReturnFeatureEnabledByCountry,
    isMktpReturnFeatureEnabled,
    isMktpReturnFeatureEnabledByCountry,
    email,
    isDealerPickupEnabled,
    isBannerNotificationEnabled,
    isCountryBannerNotificationEnabled,
    isCountryPromotionNotificationEnabled,
    isPromotionNotificationEnabled,
    notificationsEmails,
  } = action.payload;
  yield put(
    setReturnsFeatureSettings({
      isRDReturnFeatureEnabled,
      isRDReturnFeatureEnabledByCountry,
      isMktpReturnFeatureEnabled,
      isMktpReturnFeatureEnabledByCountry,
      email,
    }),
  );
  yield put(setR1Network(isDealerPickupEnabled));
  yield put(
    setPromotionSettings({
      isPromotionNotificationEnabled,
      isCountryPromotionNotificationEnabled,
      isCountryBannerNotificationEnabled,
      isBannerNotificationEnabled,
      notificationsEmails,
    }),
  );
}

export function* saveReturnsFeatureSettingsRequestSaga(): SagaIterator {
  const r1Network = yield* select(getR1Network);
  const { isRDReturnFeatureEnabled, isMktpReturnFeatureEnabled, email } = r1Network.returnFeatureSettings;
  if (email) {
    yield put(sendSaveReturnsFeatureSettingsRequest({ isRDReturnFeatureEnabled, isMktpReturnFeatureEnabled, email }));
  }
}

export function* changeR1NotifyOnPromotionRequestSaga(): SagaIterator {
  const r1Network = yield* select(getR1Network);
  const { isPromotionNotificationEnabled, notificationsEmails } = r1Network.promotionSettings;
  if (notificationsEmails.length > 0) {
    yield put(sendChangeNotifyOnNewPromotion({ isPromotionNotificationEnabled }));
  }
}

export function* changeR1NotifyOnBannerRequestSaga(): SagaIterator {
  const r1Network = yield* select(getR1Network);
  const { isBannerNotificationEnabled, notificationsEmails } = r1Network.promotionSettings;
  if (notificationsEmails.length > 0) {
    yield put(sendChangeNotifyOnNewBanner({ isBannerNotificationEnabled }));
  }
}

export function* changeR1NotifyOnCountryPromotionRequestSaga(): SagaIterator {
  const r1Network = yield* select(getR1Network);
  const { isCountryPromotionNotificationEnabled, notificationsEmails } = r1Network.promotionSettings;
  if (notificationsEmails.length > 0) {
    yield put(sendChangeNotifyOnNewCountryPromotion({ isCountryPromotionNotificationEnabled }));
  }
}

export function* changeR1NotifyOnCountryBannerRequestSaga(): SagaIterator {
  const r1Network = yield* select(getR1Network);
  const { isCountryBannerNotificationEnabled, notificationsEmails } = r1Network.promotionSettings;
  if (notificationsEmails.length > 0) {
    yield put(sendChangeNotifyOnNewCountryBanner({ isCountryBannerNotificationEnabled }));
  }
}

export function* addR1EmailToNotificationListRequestSaga({
  payload,
}: ReturnType<typeof actions.addR1EmailToNotificationListRequestSaga>): SagaIterator {
  const { email } = payload;
  if (email !== null) {
    yield put(sendAddEmailToNotificationList({ email }));
  }
}

export function* removeR1EmailFromNotificationListRequestSaga({
  payload,
}: ReturnType<typeof actions.removeR1EmailFromNotificationListRequestSaga>): SagaIterator {
  const { email } = payload;
  if (email !== null) {
    yield put(sendRemoveEmailFromNotificationList({ email }));
  }
}

export function* subscribeR1NetworkRequestSaga(): SagaIterator {
  yield put(sendSubscribeR1NetworkRequest());
  yield put(setClientConfig({ searchStatus: LOADING }));
}

export function* subscribeR1NetworkResponseSaga(action: WsResponse<GetR1NetworkResponse>): SagaIterator {
  const { isDealerPickupEnabled } = action.payload;

  yield put(
    setClientConfig({
      searchStatus: FOUND,
      data: {
        isDealerPickupEnabled,
      },
    }),
  );
}

export function* saveR1NetworkRequestSaga({
  payload,
}: ReturnType<typeof actions.saveR1NetworkRequestSaga>): SagaIterator {
  const isDealerPickupEnabled = payload.isDealerPickupEnabled;
  yield put(sendSaveR1Network({ isDealerPickupEnabled }));
}

export function* commonResponseSaga(action: WsResponse): SagaIterator {
  if (action.payload.result === 'SUCCESS') {
    yield call(
      notifyTop,
      'success',
      AppTranslation.t('backoffice.r1_network.returns_feature.command.response.success.title', 'My Network'),
      AppTranslation.t(
        'backoffice.r1_network.returns_feature.command.response.success.message',
        'Changes has been saved',
      ),
    );
  } else {
    yield call(
      notifyTop,
      'error',
      AppTranslation.t('backoffice.r1_network.returns_feature.command.response.error.title', 'My Network'),
      AppTranslation.t(
        'backoffice.r1_network.returns_feature.command.response.error.message',
        'Unable to persist changes',
      ),
    );
  }
}

export function* fetchMyNetworkClientsRequestSaga({
  payload,
}: ReturnType<typeof actions.fetchMyNetworkClientsRequestSaga>): SagaIterator {
  const pagination = yield* select(getMyNetworkClientsPagination);
  const filter = yield* select(getMyNetworkClientsFilters);
  const sort = yield* select(getMyNetworkClientsSort);
  const { page = pagination.page, pageSize = pagination.pageSize } = payload;
  const filterAndSort: FilterAndSort<MyNetworkClientsFilterAndSortField> = {
    sorts: [new SortBy(sort.field, sort.direction)],
    filter:
      filter.size > 0
        ? new FilterArray<MyNetworkClientsFilterAndSortField>('AND', getMyNetworkClientsFilterArray(filter))
        : undefined,
  };
  yield put(setMyNetworkClientsStatus(LOADING));
  yield put(setMyNetworkClientsPagination({ page, pageSize }));
  yield put(sendGetMyNetworkClients({ filterAndSort, page, pageSize }));
}

export function* updateMyNetworkClientsRequestSaga({
  payload,
}: ReturnType<typeof actions.updateMyNetworkClientsRequestSaga>): SagaIterator {
  yield put(setMyNetworkClientsSubmitting(true));
  yield put(sendUpdateMyNetworkClients(payload));
}

export function* fetchMyNetworkClientsResponseSaga(action: WsResponse<MyNetworkClientsResponse>): SagaIterator {
  yield put(setMyNetworkClients(action.payload));
}

export function* updateMyNetworkClientsResponseSaga(action: WsResponse<MyNetworkClientUpdatedResponse>): SagaIterator {
  const { client } = action.payload;
  yield put(setSelectedClient(client));
  yield put(setMyNetworkClientsSubmitting(false));
  yield call(
    notifyTop,
    'success',
    AppTranslation.t('my_network.garage_details.save.success.title', 'Client profile has been saved successfully'),
  );
}

export function* R1NetworkSagas(): SagaIterator {
  yield takeEvery(actions.getR1NetworkRequestSaga.type, sagaGuard(getR1NetworkRequestSaga));
  yield takeEvery(actions.getR1NetworkResponseSaga.type, sagaGuard(getR1NetworkResponseSaga));
  yield takeEvery(actions.subscribeR1NetworkRequestSaga.type, sagaGuard(subscribeR1NetworkRequestSaga));
  yield takeEvery(actions.subscribeR1NetworkResponseSaga.type, sagaGuard(subscribeR1NetworkResponseSaga));
  yield takeEvery(actions.saveReturnsFeatureSettingsRequestSaga.type, sagaGuard(saveReturnsFeatureSettingsRequestSaga));
  yield takeEvery(actions.saveR1NetworkRequestSaga.type, sagaGuard(saveR1NetworkRequestSaga));
  yield takeEvery(actions.saveReturnsFeatureSettingsResponseSaga.type, sagaGuard(commonResponseSaga));

  yield takeEvery(actions.changeR1NotifyOnPromotionRequestSaga.type, sagaGuard(changeR1NotifyOnPromotionRequestSaga));
  yield takeEvery(
    actions.changeR1NotifyOnCountryPromotionRequestSaga.type,
    sagaGuard(changeR1NotifyOnCountryPromotionRequestSaga),
  );
  yield takeEvery(actions.changeR1NotifyOnBannerRequestSaga.type, sagaGuard(changeR1NotifyOnBannerRequestSaga));
  yield takeEvery(
    actions.changeR1NotifyOnCountryBannerRequestSaga.type,
    sagaGuard(changeR1NotifyOnCountryBannerRequestSaga),
  );
  yield takeEvery(
    actions.addR1EmailToNotificationListRequestSaga.type,
    sagaGuard(addR1EmailToNotificationListRequestSaga),
  );
  yield takeEvery(
    actions.removeR1EmailFromNotificationListRequestSaga.type,
    sagaGuard(removeR1EmailFromNotificationListRequestSaga),
  );

  yield takeEvery(actions.changeR1NotifyOnPromotionResponseSaga.type, sagaGuard(commonResponseSaga));
  yield takeEvery(actions.changeR1NotifyOnCountryPromotionResponseSaga.type, sagaGuard(commonResponseSaga));
  yield takeEvery(actions.changeR1NotifyOnBannerResponseSaga.type, sagaGuard(commonResponseSaga));
  yield takeEvery(actions.changeR1NotifyOnCountryBannerResponseSaga.type, sagaGuard(commonResponseSaga));
  yield takeEvery(actions.addR1EmailToNotificationListResponseSaga.type, sagaGuard(commonResponseSaga));
  yield takeEvery(actions.removeR1EmailFromNotificationListResponseSaga.type, sagaGuard(commonResponseSaga));
  yield takeEvery(actions.fetchMyNetworkClientsRequestSaga.type, sagaGuard(fetchMyNetworkClientsRequestSaga));
  yield takeEvery(actions.updateMyNetworkClientsRequestSaga.type, sagaGuard(updateMyNetworkClientsRequestSaga));
  yield takeEvery(actions.fetchMyNetworkClientsResponseSaga.type, sagaGuard(fetchMyNetworkClientsResponseSaga));
  yield takeEvery(actions.updateMyNetworkClientsResponseSaga.type, sagaGuard(updateMyNetworkClientsResponseSaga));
}
